import { useCallback, useMemo, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { ROUTES, VALIDATION_MESSAGES } from '@/shared/lib/constants';
import { BadgeList } from '@/shared/ui/BadgeList';
import { Icon } from '@/shared/ui/Icon';
import { MetadataEditCard } from '@/shared/ui/MetadataEditCard';
import { MetadataEditModal } from '@/shared/ui/MetadataEditModal';
import { OwnerCard } from '@/shared/ui/OwnerCard';

import { useDisplaySettingsStore } from '@/entities/display-settings';
import { DomainTree } from '@/entities/domain';
import type { TagItem } from '@/entities/tags';

import type { GlossaryTermDetailResponse } from '@/features/glossary/api';
import { useGlossaryOwnerEdit, useGlossaryTagsSelect } from '@/features/glossary/model';
import { useGlossaryDomainEdit } from '@/features/glossary/model';
import { useGlossaryTermCategoryInfo } from '@/features/glossary/model/useGlossaryTermCategoryInfo';
import { useGlossaryTermDomainEdit } from '@/features/glossary/model/useGlossaryTermDomainEdit';

type GlossaryMetadataSidebarProps = {
  termData?: GlossaryTermDetailResponse | null;
  allTags?: TagItem[];
};

export const GlossaryMetadataSidebar = ({ termData, allTags }: GlossaryMetadataSidebarProps = {}) => {
  const { termCategory, termFqn } = useParams<{ termCategory?: string; termFqn?: string }>();
  const { data: categoryInfo } = useGlossaryTermCategoryInfo({ category: termCategory ?? '' });
  const showOwners = useDisplaySettingsStore((state) => state.showOwners);
  const navigate = useNavigate();

  const dataSource = termData || categoryInfo;
  const isTermDetail = !!termData && !!termFqn;

  const fullTermFqn = useMemo(() => {
    if (!termFqn || !termCategory) return null;
    return `${termCategory}.${termFqn}`;
  }, [termCategory, termFqn]);

  const owners = useMemo(() => dataSource?.owners || [], [dataSource]);
  const ownerEdit = useGlossaryOwnerEdit({
    glossaryId: dataSource?.id ?? '',
    glossaryCategory: termCategory,
  });
  const ownerDisabled = useMemo(() => {
    return !dataSource?.id || !termCategory;
  }, [dataSource?.id, termCategory]);

  const tagsToUse = termData ? allTags : categoryInfo?.tags;

  const {
    tagsTreeData,
    isUpdating: isUpdatingTags,
    handleSaveTags,
  } = useGlossaryTagsSelect(dataSource?.id ?? '', termCategory ?? '', tagsToUse ?? []);

  const onBadgeClick = useCallback(
    (tagFQN: string) => {
      navigate(`${ROUTES.TAGS_DETAIL}/${encodeURIComponent(tagFQN)}`);
    },
    [navigate]
  );

  const existingTagFQNs = useMemo(() => {
    if (!tagsToUse) return new Set<string>();
    return new Set(tagsToUse.map((tag) => tag.tagFQN));
  }, [tagsToUse]);

  const [isOpenEditTagModal, setIsOpenEditTagModal] = useState(false);

  const tagTreeSelectValue = useMemo(() => {
    if (!tagsToUse) return [];
    return tagsToUse.map((tag) => ({ value: tag.tagFQN, label: tag.displayName ?? tag.name }));
  }, [tagsToUse]);

  const renderTagBadgeList = useCallback(() => {
    if (!tagsToUse || tagsToUse.length === 0) return null;

    const tagBadgeList = tagsToUse.map((tag) => ({
      tagFQN: tag.tagFQN,
      name: tag.name,
      displayName: tag.displayName,
      description: tag.description,
      style: tag.style,
    }));

    return <BadgeList items={tagBadgeList} onItemClick={onBadgeClick} existingValues={existingTagFQNs} />;
  }, [tagsToUse, onBadgeClick, existingTagFQNs]);

  const domainEditCategory = useGlossaryDomainEdit({
    glossaryCategoryId: dataSource?.id ?? '',
    categoryName: termCategory ?? '',
  });

  const domainEditTerm = useGlossaryTermDomainEdit({
    termId: termData?.id ?? '',
    termFqn: fullTermFqn ?? '',
    termCategory: termCategory ?? '',
  });

  const domainEdit = isTermDetail ? domainEditTerm : domainEditCategory;

  const domainData = useMemo(() => {
    if (!dataSource?.domains) return [];
    return dataSource.domains.map((domain) => ({
      id: domain.id,
      type: domain.type,
      name: domain.name,
      fullyQualifiedName: domain.fullyQualifiedName,
      description: domain.description || '',
      displayName: domain.displayName || domain.name,
      href: '',
    }));
  }, [dataSource?.domains]);

  return (
    <>
      {showOwners && <OwnerCard owners={owners} ownerEdit={ownerEdit} disabled={ownerDisabled} />}
      <MetadataEditCard
        title="태그"
        renderBadgeList={renderTagBadgeList}
        handleEdit={() => setIsOpenEditTagModal(true)}
        emptyText="태그가 없습니다"
      />
      <MetadataEditModal
        checkStrictly={true}
        open={isOpenEditTagModal}
        icon={<Icon name="tag" size={12} />}
        title="태그 편집"
        firstLabel="선택된 태그"
        secondLabel="태그"
        searchValidation={{
          maxLength: 30,
          warningMessage: VALIDATION_MESSAGES.TAG_SEARCH_MAX_LENGTH,
        }}
        loading={isUpdatingTags}
        treeData={tagsTreeData}
        treeSelectValue={tagTreeSelectValue}
        onClose={() => setIsOpenEditTagModal(false)}
        onSubmit={handleSaveTags}
      />
      <DomainTree
        title="도메인"
        domainData={domainData}
        onSave={domainEdit.handleSave}
        isUpdating={domainEdit.isUpdating}
      />
    </>
  );
};
